import React from 'react'
import { useApp } from '../../context/AppContext'
import { GroupContext } from './SettingsGroup'
import { useDialog } from '../../hooks/useDialog'
import './Settings.css'
import { BackLink } from '../../ui/Icon'
import { Loading } from '../../ui/States'

interface PanelProps {
  title: string
  intro?: string
  // unsavedChanges asks before going back, so edits aren't lost by accident.
  unsavedChanges?: boolean
  children: React.ReactNode
}

// Panel is a full-screen page over Settings with a way back.
export default function Panel({ title, intro, unsavedChanges = false, children }: PanelProps) {
  const { dispatch } = useApp()
  const group = React.useContext(GroupContext)
  React.useEffect(() => {
    group?.setUnsaved(title, unsavedChanges)
    return () => group?.setUnsaved(title, false)
  }, [group, title, unsavedChanges])

  const dialogRef = React.useRef<HTMLDivElement>(null)
  const back = () => {
    if (unsavedChanges && !window.confirm('Leave without saving? Your changes will be lost.')) {
      return
    }
    dispatch({ type: 'CLOSE_SETTINGS_PANEL' })
  }
  // A section inside a group is not a dialog of its own.
  useDialog(dialogRef, back, { active: !group })

  // Inside a Settings group the panel is one section of the page.
  if (group) {
    // The only section of a page of the same name: the page's heading is its heading.
    if (group.title === title) {
      return (
        <section className="settings-section" aria-label={title}>
          {intro && <p className="settings-intro">{intro}</p>}
          {children}
        </section>
      )
    }
    const id = `settings-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    return (
      <section className="settings-section" aria-labelledby={id}>
        <h2 id={id} className="settings-section__title">{title}</h2>
        {intro && <p className="settings-intro">{intro}</p>}
        {children}
      </section>
    )
  }

  return (
    <div ref={dialogRef} className="settings-panel" role="dialog" aria-modal="true" aria-label={title}>
      <div className="menu-header">
        <BackLink onClick={back}>Settings</BackLink>
      </div>
      <div className="settings-body">
        <h1 className="menu-title">{title}</h1>
        {intro && <p className="settings-intro">{intro}</p>}
        {children}
      </div>
    </div>
  )
}

export function PanelStatus({ status, onRetry }: { status: 'loading' | 'error'; onRetry: () => void }) {
  if (status === 'loading') {
    return <Loading />
  }
  return (
    <div className="settings-status" role="alert">
      <p>Couldn’t load your settings. Check your connection and try again.</p>
      <button type="button" className="btn btn-secondary btn-compact" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}
